import {
  CreateDatasetFromHistory,
  CreateSimulationDatasetJob,
} from "src/api/flowTypes";
import {
  DecisionEnvironment,
  DownloadDatasetJobRequest,
  DuplicateDatasetJobRequest,
  EventUploadDatasetJobRequest,
  ExportDecisionsDatasetJobRequest,
  HistoryExportDatasetJobRequest,
  SimulationDownloadDatasetJobRequest,
  SimulationFailuresDatasetJobRequest,
} from "src/api/types";
import {
  EntityJobSourceConfigurationOutput,
  JobRun as GeneratedJobRun,
  DecisionEnvironment as JobRunDecisionEnvironment,
} from "src/clients/jobs";

export type JobStatus = "active" | "inactive";

export type JobProbabilisticTrafficPolicyVersionEntry = {
  flow_version_id: string;
  percentage: number;
};

export type JobProbabilisticTrafficPolicy = {
  type: "probabilistic";
  flow_versions: JobProbabilisticTrafficPolicyVersionEntry[];
};
export type JobTrafficPolicy = {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  policy: JobProbabilisticTrafficPolicy;
  stable_routing: boolean;
};

export type Job = {
  id: string;
  name: string;
  description?: string;
  status: JobStatus;
  flow_id: string;
  flow_version_id?: string;
  schedule?: Nullable<string>;
  schedule_timezone?: Nullable<string>;
  active_source?: JobSource;
  active_traffic_policy?: JobTrafficPolicy;
  active_destination?: JobDestination;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  etag: string;
  last_run_at?: string;
  live_runs_count: number;
  sandbox_runs_count: number;
  configuration?: JobConfiguration;
  tags?: JobTags;
};

export type JobConfigurationExecutionMode = "sync" | "async";

export type JobConfigurationRateLimit = {
  rate_limit: number;
  rate_limit_interval: JobRateLimitInterval;
  execution_concurrency?: null;
  execution_mode: JobConfigurationExecutionMode;
};

export type JobConfigurationExecutionConcurrency = {
  execution_concurrency: number;
  rate_limit?: null;
  rate_limit_interval?: null;
  execution_mode: JobConfigurationExecutionMode;
};

export type JobConfiguration =
  | JobConfigurationRateLimit
  | JobConfigurationExecutionConcurrency;

export enum JobRateLimitInterval {
  MILLISECOND = "MILLISECOND",
  SECOND = "SECOND",
  MINUTE = "MINUTE",
  HOUR = "HOUR",
}

type JobSourceBase = {
  id: string;
  etag: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  job_id: string;
  name: string;
};

export type DatasetJobSourceConfiguration = {
  type: "dataset";
  dataset_id: string;
};

export type S3BucketDatasetJobSourceConfiguration = {
  type: "s3_bucket_dataset";
  s3_bucket_dataset_id: string;
};

type SQLJobSourceConfigurationMappingEntry = {
  db_field: Nullable<string>;
  flow_input_field: string;
};

export type SQLJobSourceConfiguration = {
  type: "sql";
  connection_id: Nullable<string>;
  query: Nullable<string>;
  mapping: SQLJobSourceConfigurationMappingEntry[];
  metadata_mapping: SQLJobSourceConfigurationMappingEntry[];
  credentials_for_sandbox_run: "sandbox_credentials" | "live_credentials";
};

export type JobSQLSource = JobSourceBase & {
  configuration: SQLJobSourceConfiguration;
};

export type JobDatasetSource = JobSourceBase & {
  configuration: DatasetJobSourceConfiguration;
};

export type JobS3BucketDatasetSource = JobSourceBase & {
  configuration: S3BucketDatasetJobSourceConfiguration;
};

export type JobEntitySource = JobSourceBase & {
  configuration: EntityJobSourceConfigurationOutput;
};

export type JobSource =
  | JobSQLSource
  | JobDatasetSource
  | JobEntitySource
  | JobS3BucketDatasetSource;

export type CreateJobDestination = Pick<
  JobDestination,
  "name" | "configuration"
>;

type JobDestinationBase = {
  id: string;
  etag: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  job_id: string;
  name: string;
};

export type JobDBDestinationConfiguration = {
  type: "sql";
  connection_id?: string;
  table_prefix?: string;
  credentials_for_sandbox_run?: "live_credentials" | "sandbox_credentials";
  mapping: {
    db_field: string;
    flow_output_field: string;
  }[];
};

export type JobS3BucketDestinationConfiguration = {
  type: "s3_bucket";
};

export type JobDestinationDB = JobDestinationBase & {
  configuration: JobDBDestinationConfiguration;
};

export type JobDestinationS3Bucket = JobDestinationBase & {
  configuration: JobS3BucketDestinationConfiguration;
};

export type JobDestination = JobDestinationDB | JobDestinationS3Bucket;

export type JobWithVersionsUsed = Job & {
  versionsUsed: { versionName: string; percentage: number }[];
};

export enum JobRunStatusType {
  Completed = "completed",
  Running = "running",
  Failed = "failed",
  Stopped = "stopped",
  Writing = "writing",
}

type CompletedJobRunStatus = {
  type: JobRunStatusType.Completed;
  completed_at?: string;
};

type RunningJobRunStatus = {
  type: JobRunStatusType.Running;
};

type FailedJobRunStatus = {
  type: JobRunStatusType.Failed;
  error_title: string;
  error_details: string;
};

type StoppedJobRunStatus = {
  type: JobRunStatusType.Stopped;
  stopped_by: string;
};

type WritingJobRunStatus = {
  type: JobRunStatusType.Writing;
};

export type JobRunStatus =
  | CompletedJobRunStatus
  | RunningJobRunStatus
  | FailedJobRunStatus
  | StoppedJobRunStatus
  | WritingJobRunStatus;

export const getJobRunFlags = (statusType: JobRunStatusType | undefined) => ({
  isJobRunning:
    statusType === JobRunStatusType.Running ||
    statusType === JobRunStatusType.Writing,
  isJobTerminated:
    statusType === JobRunStatusType.Failed ||
    statusType === JobRunStatusType.Stopped,
});

type ManualJobRunTrigger = {
  type: "manual";
  triggered_by: string;
};

type ScheduleJobRunTrigger = {
  type: "schedule";
  schedule: string;
  service_account_id: string;
};

type JobTrigger = ManualJobRunTrigger | ScheduleJobRunTrigger;

type JobTags = {
  simulation: boolean;
  test_run: boolean;
};

export type JobRun = {
  id: string;
  created_at: string;
  updated_at: string;
  job_id: string;
  job_etag: string;
  flow_version_id?: string;
  traffic_policy?: JobTrafficPolicy;
  source_id: string;
  source_name: string;
  source_etag: string;
  source_type: "dataset";
  destination_id: string;
  destination_name: string;
  destination_etag: string;
  destination_type: JobDestination["configuration"]["type"];
  environment: JobRunDecisionEnvironment;
  trigger: JobTrigger;
  status: JobRunStatus;

  tags?: JobTags;
} & Pick<
  GeneratedJobRun,
  | "simulation_job_run_details"
  | "success_rows_count"
  | "failed_rows_count"
  | "pending_rows_count"
  | "total_rows_count"
  | "ignored_rows_count"
  | "output_mismatch_rows_count"
>;

/**
 * Converts a JobRunDecisionEnvironment (LIVE or SANDBOX) to a DecisionEnvironment.
 * This is a safe cast because both enums have the same possible values for these environments.
 */
export const JobRunEnvironmentToDecisionEnvironment = (
  env: JobRunDecisionEnvironment.LIVE | JobRunDecisionEnvironment.SANDBOX,
): DecisionEnvironment => {
  return env as DecisionEnvironment;
};

type DatasetJobCommon = {
  id: string;
  created_at: string;
  updated_at: string;
  progress: number;
  created_by: string;
  status: "FAILED" | "COMPLETED" | "PENDING" | "PARTIALLY_COMPLETED";
  flow_id: string;
};

export type CreateDuplicateDatasetJob = {
  flow_id: string;
  request: DuplicateDatasetJobRequest;
};
export type CreateDownloadDatasetJob = {
  flow_id: string;
  request: DownloadDatasetJobRequest;
};
export type CreateSimulationFailuresDatasetJob = {
  flow_id: string;
  request: SimulationFailuresDatasetJobRequest;
};
export type CreateHistoryExportDatasetJob = {
  flow_id: string;
  request: HistoryExportDatasetJobRequest;
};

export type DuplicateDatasetJob = DatasetJobCommon & {
  type: "duplicate";
  request: DuplicateDatasetJobRequest;
  error?: { message: string };
};

export type AssembleDatasetJob = DatasetJobCommon & {
  type: "assemble";
  request: CreateDatasetFromHistory;
  error?: { message: string };
};

export type AssembleSimulationDatasetJob = DatasetJobCommon & {
  type: "assemble_simulation";
  request: CreateSimulationDatasetJob;
  error?: { message: string };
};

export type DownloadDatasetJob = DatasetJobCommon & {
  type: "download";
  request: DownloadDatasetJobRequest;
  error?: { message: string };
  response?: { s3_url: string };
};

export type ExportDecisionsDatasetJob = DatasetJobCommon & {
  type: "export_decisions";
  request: ExportDecisionsDatasetJobRequest;
  error?: { message: string };
  response?: { s3_url: string };
};

export type HistoryExportDatasetJob = DatasetJobCommon & {
  type: "history_export";
  request: HistoryExportDatasetJobRequest;
  error: { message: string };
  response?: { s3_url: string };
};
export type EventUploadDatasetJob = DatasetJobCommon & {
  type: "event_upload";
  request: EventUploadDatasetJobRequest;
  error?: { message: string };
  response?: {
    s3_presigned_url: string;
    s3_presigned_fields: Record<string, string>;
  };
};
export type SimulationFailuresDatasetJob = DatasetJobCommon & {
  type: "simulation_failures";
  request: SimulationFailuresDatasetJobRequest;
  error?: { message: string };
};
export type SimulationDownloadDatasetJob = DatasetJobCommon & {
  type: "simulation_download";
  request: SimulationDownloadDatasetJobRequest;
  error?: { message: string };
  response?: { s3_url: string };
};

export type DatasetJob =
  | DuplicateDatasetJob
  | SimulationDownloadDatasetJob
  | AssembleDatasetJob
  | DownloadDatasetJob
  | HistoryExportDatasetJob
  | EventUploadDatasetJob
  | AssembleSimulationDatasetJob
  | SimulationFailuresDatasetJob;

export const isAssembleOrDuplicateJob = (
  job: DatasetJob,
): job is
  | DuplicateDatasetJob
  | AssembleDatasetJob
  | AssembleSimulationDatasetJob
  | SimulationFailuresDatasetJob => {
  return (
    job.type === "duplicate" ||
    job.type === "assemble" ||
    job.type === "assemble_simulation" ||
    job.type === "simulation_failures"
  );
};

export type CreateDatasetJobPayload =
  | {
      type: "assemble_simulation";
      body: { flow_id: string; request: CreateSimulationDatasetJob };
    }
  | {
      type: "assemble";
      body: { flow_id: string; request: CreateDatasetFromHistory };
    }
  | { type: "duplicate"; body: CreateDuplicateDatasetJob }
  | { type: "simulation_failures"; body: CreateSimulationFailuresDatasetJob }
  | {
      type: "export_decisions";
      body: { flow_id: string; request: ExportDecisionsDatasetJobRequest };
    }
  | { type: "download"; body: CreateDownloadDatasetJob }
  | {
      type: "create_job_source";
      body: {
        flow_id: string;
        request: { name: string; status_codes: string[]; job_run_id: string };
      };
    }
  | { type: "event_upload"; body: { request: EventUploadDatasetJobRequest } }
  | {
      type: "simulation_download";
      body: { flow_id: string; request: SimulationDownloadDatasetJobRequest };
    };

export type CreateDatasetJobReturnType<
  T extends CreateDatasetJobPayload["type"],
> = T extends "duplicate"
  ? DuplicateDatasetJob
  : T extends "assemble"
    ? AssembleDatasetJob
    : T extends "assemble_simulation"
      ? AssembleSimulationDatasetJob
      : T extends "simulation_failures"
        ? SimulationFailuresDatasetJob
        : T extends "export_decisions"
          ? ExportDecisionsDatasetJob
          : T extends "download"
            ? DownloadDatasetJob
            : T extends "event_upload"
              ? EventUploadDatasetJob
              : T extends "simulation_download"
                ? SimulationDownloadDatasetJob
                : T extends "history_export"
                  ? HistoryExportDatasetJob
                  : DatasetJob;

export type TestRunJobRun = JobRun & { trigger: { type: "manual" } };
